import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import cotizacionVentaService, { CotizacionVenta } from '../../../services/cotizacionVentaService';
import { formatCurrency } from '../../../utils/formatters';
import { getApiErrorMessage } from '../../../utils/apiError';

const ESTADOS_ABIERTOS = ['Nuevo', 'Impreso', 'Preparado'] as const;

export interface CrmVincularCotizacionDialogProps {
  open: boolean;
  excludeNros?: number[];
  onClose: () => void;
  onConfirm: (nros: number[]) => void;
}

type CotizacionRow = {
  nroFormulario: number;
  nombre?: string;
  nroCliente?: number;
  estado?: string;
  fechaEmision?: string | Date;
  importeTotal?: number;
};

function formatFecha(value?: string | Date): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-AR');
}

function isAbierta(estado?: string | null): boolean {
  const key = (estado || 'Nuevo').trim();
  return (ESTADOS_ABIERTOS as readonly string[]).includes(key);
}

function mapRow(c: CotizacionVenta): CotizacionRow {
  return {
    nroFormulario: c.nroFormulario,
    nombre: c.nombre,
    nroCliente: c.nroCliente,
    estado: c.estado,
    fechaEmision: c.fechaEmision,
    importeTotal: c.importeTotal,
  };
}

const CrmVincularCotizacionDialog: React.FC<CrmVincularCotizacionDialogProps> = ({
  open,
  excludeNros = [],
  onClose,
  onConfirm,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<CotizacionRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const excludedKey = useMemo(
    () =>
      excludeNros
        .filter((n) => n > 0)
        .slice()
        .sort((a, b) => a - b)
        .join(','),
    [excludeNros],
  );
  const excluded = useMemo(
    () =>
      new Set(
        excludedKey
          ? excludedKey.split(',').map((x) => Number(x)).filter((n) => n > 0)
          : [],
      ),
    [excludedKey],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const term = search.trim() || undefined;
      const pages = await Promise.all(
        ESTADOS_ABIERTOS.map((estado) =>
          cotizacionVentaService.getCotizacionesVenta({
            pageNumber: 1,
            pageSize: 20,
            searchTerm: term,
            estado,
          }),
        ),
      );
      const merged = pages
        .flatMap((res) => (res.items || []).filter((c) => isAbierta(c.estado)).map(mapRow))
        .filter((r) => !excluded.has(r.nroFormulario));

      const seen = new Set<number>();
      const unique: CotizacionRow[] = [];
      for (const row of merged) {
        if (seen.has(row.nroFormulario)) continue;
        seen.add(row.nroFormulario);
        unique.push(row);
      }
      unique.sort((a, b) => {
        const ta = a.fechaEmision ? new Date(a.fechaEmision).getTime() : 0;
        const tb = b.fechaEmision ? new Date(b.fechaEmision).getTime() : 0;
        return tb - ta;
      });
      setRows(unique);
    } catch (e) {
      const message = getApiErrorMessage(e, 'Error al cargar cotizaciones');
      setError(message);
      setRows([]);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, excluded, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setError(null);
    setRows([]);
    setSelected(new Set());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => {
      void load();
    }, search ? 350 : 50);
    return () => window.clearTimeout(t);
  }, [open, load, search]);

  const toggle = (nro: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nro)) next.delete(nro);
      else next.add(nro);
      return next;
    });
  };

  const handleConfirm = () => {
    const nros = Array.from(selected);
    if (nros.length === 0) return;
    onConfirm(nros);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vincular cotizaciones</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Buscar por nro., comprobante, cliente o nombre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1, mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Cotizaciones abiertas (Nuevo, Impreso, Preparado). Las ya vinculadas no se listan.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 && !error ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No hay cotizaciones abiertas para mostrar.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600 }}>Nro.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Importe
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const checked = selected.has(row.nroFormulario);
                  return (
                    <TableRow
                      key={row.nroFormulario}
                      hover
                      selected={checked}
                      onClick={() => toggle(row.nroFormulario)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggle(row.nroFormulario)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>{row.nroFormulario}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatFecha(row.fechaEmision)}
                      </TableCell>
                      <TableCell>
                        {row.nroCliente ? `${row.nroCliente} — ` : ''}
                        {row.nombre || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.estado || 'Nuevo'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.importeTotal || 0)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={() => void load()} disabled={loading}>
          Actualizar
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selected.size === 0}>
          Vincular{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrmVincularCotizacionDialog;
